export type Store<T = unknown> = {
  /**
   * Returns the value from the store.
   */
  get: () => T;

  /**
   * Sets a new value in the store.
   */
  set: (value: SetValueType<T>) => void;

  /**
   * Adds a change listener to the store.
   */
  onChange: (handleChange: ChangeListener<T>) => () => void;
};

type ChangeListener<T> = (value: T) => void;

type SetValueType<T> = T | ((prev: T) => T);

function valueIsClosure<T>(value: SetValueType<T>): value is (prev: T) => T {
  return typeof value === 'function';
}

export function createStore<T>(initialValue: T): Store<T>;
export function createStore<T = undefined>(): Store<T | undefined>;

/**
 * Creates a store. You can utilize the store for simple shared state.
 *
 * ### Create a Store
 *
 * Make sure to create your store only once and save it in a dedicated file.
 *
 * ```tsx
 * // my-count-store.ts
 *
 * export const myCountStore = createStore<number>(0);
 * ```
 *
 * ### Hook
 *
 * Usually you would pair the store with the usage of the {@link useStore} hook -- head over to learn how to use a created store in your components.
 *
 * The hook abstracts all the React related boilerplate away, so you can use the store like a simple `useState()`.
 */
export function createStore<T = undefined>(
  initialValue?: T,
): Store<T | undefined> {
  let value = initialValue;

  const listeners: ChangeListener<T | undefined>[] = [];

  return {
    get: () => value,
    set: newValue => {
      if (valueIsClosure(newValue)) {
        value = newValue(value);
      } else {
        value = newValue;
      }

      listeners.forEach(listener => listener(value));
    },
    onChange: handleChange => {
      listeners.push(handleChange);
      const idx = listeners.length - 1;

      return () => {
        listeners.splice(idx, 1);
      };
    },
  };
}
